import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { isAdminRole, type Role } from '../api.ts';
import { t } from '../i18n/index.ts';
import {
  EvidenceReasonRequired,
  listAssets,
  type EvidenceAsset,
  type ReasonChallenge,
} from '../lib/evidence.ts';
import {
  createDisclosure,
  getDisclosure,
  getDisclosureBundle,
  listCases,
  listDisclosures,
  revokeDisclosure,
  type DisclosureDetail,
  type DisclosureListRow,
  type DisclosureRow,
  type SealedBundle,
} from '../lib/evidence-workspace.ts';
import {
  Button,
  Cell,
  Empty,
  Field,
  Finding,
  Label,
  Metric,
  Metrics,
  Mono,
  Row,
  Section,
  Select,
  Spinner,
  Status,
  Table,
  sentence,
  type Tone,
} from '../ui.tsx';
import { EvidenceGate, download, formatWhen, shortId } from './evidence-gate.tsx';

/**
 * Disclosure — seal a court-ready bundle of evidence, hand it over, and revoke
 * it if it should no longer stand.
 *
 * Court-grade honesty is the whole point of this surface: the sealed bundle's
 * verdict is STRUCTURE_VERIFIED (append-only structure + signatures), NOT
 * one-true-log, because the transparency log is unwitnessed in v1. The screen
 * states that caveat plainly — before the download and inside the certificate
 * it renders verbatim from the service — rather than implying more proof than
 * the service actually gives (`evac-evidence-service` `src/evidence/disclosure.ts`).
 */

/** The honest one-line summary of what a v1 bundle proves — matches the service. */
const VERDICT_NOTE =
  'A disclosure bundle proves append-only structure and signatures — verdict ceiling ' +
  'STRUCTURE_VERIFIED. The transparency log is unwitnessed in v1, so split-view protection ' +
  'is not yet established; treat log inclusion as tamper-evident structure, not one-true-log, ' +
  'until independent witnesses are lodged with the court or agency.';

type DisclosureState = 'active' | 'revoked' | 'expired';

function disclosureState(d: {
  revoked_at: string | null;
  expires_at: string | null;
}): DisclosureState {
  if (d.revoked_at !== null) return 'revoked';
  if (d.expires_at !== null && Date.parse(d.expires_at) < Date.now()) return 'expired';
  return 'active';
}

const STATE_TONE: Record<DisclosureState, Tone> = {
  active: 'safe',
  revoked: 'alert',
  expired: 'attention',
};

export function Disclosure({
  orgId,
  role,
  onGoToEvidence,
}: {
  orgId: string;
  role: Role;
  onGoToEvidence?: () => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<ReasonChallenge | null>(null);
  const canWrite = isAdminRole(role);

  const disclosures = useQuery({
    queryKey: ['evidence-disclosures', orgId],
    queryFn: async () => {
      try {
        return await listDisclosures(orgId);
      } catch (e) {
        if (e instanceof EvidenceReasonRequired) {
          setChallenge(e.challenge);
          return { items: [], next_cursor: null };
        }
        throw e;
      }
    },
    retry: false,
  });

  const onStated = () => {
    setChallenge(null);
    void qc.invalidateQueries({ queryKey: ['evidence-disclosures', orgId] });
  };

  return (
    <EvidenceGate
      title={t('disclosure.title')}
      sub={t('disclosure.sub')}
      err={disclosures.error}
      loading={disclosures.isLoading}
      challenge={challenge}
      onStated={onStated}
      onGoToEvidence={onGoToEvidence}
    >
      {selected !== null ? (
        <DisclosureView
          orgId={orgId}
          disclosureId={selected}
          canWrite={canWrite}
          onBack={() => setSelected(null)}
        />
      ) : (
        <DisclosureList
          orgId={orgId}
          canWrite={canWrite}
          list={disclosures.data?.items ?? []}
          onOpen={setSelected}
        />
      )}
    </EvidenceGate>
  );
}

function DisclosureList({
  orgId,
  canWrite,
  list,
  onOpen,
}: {
  orgId: string;
  canWrite: boolean;
  list: DisclosureListRow[];
  onOpen: (id: string) => void;
}) {
  const active = list.filter((d) => disclosureState(d) === 'active').length;

  return (
    <>
      <div className="mb-6">
        <Metrics>
          <Metric label={t('disclosure.metric.disclosures')} value={list.length} />
          <Metric
            label={t('disclosure.metric.active')}
            value={active}
            tone={active > 0 ? 'safe' : 'neutral'}
          />
        </Metrics>
      </div>

      {canWrite ? <NewDisclosure orgId={orgId} onCreated={onOpen} /> : null}

      <Section title={t('disclosure.list.title')}>
        {list.length === 0 ? (
          <Empty>{t('disclosure.list.empty')}</Empty>
        ) : (
          <Table
            head={[
              t('disclosure.head.recipient'),
              t('disclosure.head.purpose'),
              t('disclosure.head.method'),
              t('disclosure.head.items'),
              t('disclosure.head.created'),
              t('disclosure.head.state'),
              '',
            ]}
          >
            {list.map((d) => {
              const s = disclosureState(d);
              return (
                <Row key={d.id}>
                  <Cell wide>{d.recipient}</Cell>
                  <Cell>{d.purpose}</Cell>
                  <Cell>{d.method}</Cell>
                  <Cell numeric>{d.item_count}</Cell>
                  <Cell>{formatWhen(d.created_at)}</Cell>
                  <Cell>
                    <Status tone={STATE_TONE[s]}>{sentence(s)}</Status>
                  </Cell>
                  <Cell>
                    <Button
                      variant="quiet"
                      label={`Open disclosure to ${d.recipient}`}
                      onClick={() => onOpen(d.id)}
                    >
                      {t('disclosure.open')}
                    </Button>
                  </Cell>
                </Row>
              );
            })}
          </Table>
        )}
      </Section>
    </>
  );
}

function NewDisclosure({ orgId, onCreated }: { orgId: string; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const [recipient, setRecipient] = useState('');
  const [purpose, setPurpose] = useState('');
  const [method, setMethod] = useState('');
  const [source, setSource] = useState<'case' | 'assets'>('case');
  const [caseId, setCaseId] = useState('');
  const [assetIds, setAssetIds] = useState<Set<string>>(new Set());
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState('');

  const cases = useQuery({
    queryKey: ['evidence-cases', orgId],
    queryFn: () => listCases(orgId),
    retry: false,
  });
  const assets = useQuery({
    queryKey: ['evidence-assets', orgId],
    queryFn: () => listAssets(orgId),
    retry: false,
  });

  const create = useMutation({
    mutationFn: () =>
      createDisclosure(orgId, {
        recipient: recipient.trim(),
        purpose: purpose.trim(),
        method: method.trim(),
        ...(source === 'case' ? { caseId } : { assetIds: [...assetIds] }),
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
      }),
    onSuccess: (created) => {
      setRecipient('');
      setPurpose('');
      setMethod('');
      setCaseId('');
      setAssetIds(new Set());
      setExpiresAt('');
      setError('');
      void qc.invalidateQueries({ queryKey: ['evidence-disclosures', orgId] });
      onCreated(created.disclosure.id);
    },
    onError: (e: Error) => setError(e.message),
  });

  const storedAssets = (assets.data?.items ?? []).filter(
    (a: EvidenceAsset) => a.state === 'stored',
  );
  const openCases = cases.data?.items ?? [];
  const sourceReady = source === 'case' ? Boolean(caseId) : assetIds.size > 0;
  const ready = recipient.trim() && purpose.trim() && method.trim() && sourceReady;

  const toggleAsset = (id: string) => {
    setAssetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Section title={t('disclosure.new.title')}>
      <p className="mb-4 max-w-prose text-[14px] text-[var(--ink-2)]">
        Sealing a disclosure signs a receipt naming the recipient, the purpose, the method, and
        exactly what was disclosed. Only committed (stored) evidence can be disclosed.
      </p>
      {error ? <Finding tone="alert">{error}</Finding> : null}
      <form
        className="flex max-w-[640px] flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) create.mutate();
        }}
      >
        <Field
          label={t('disclosure.new.field.recipient')}
          value={recipient}
          onChange={setRecipient}
          placeholder="Name of the person or body receiving it"
        />
        <Field
          label={t('disclosure.new.field.purpose')}
          value={purpose}
          onChange={setPurpose}
          placeholder="Why it is being disclosed"
          hint="Recorded on the certificate — state the lawful basis (e.g. PPIP s.18)."
        />
        <Field
          label={t('disclosure.new.field.method')}
          value={method}
          onChange={setMethod}
          placeholder="How it is handed over — secure link, physical media…"
        />

        <Select
          label={t('disclosure.new.field.what')}
          value={source}
          onChange={(v) => setSource(v as 'case' | 'assets')}
          options={[
            { value: 'case', label: 'All evidence linked to a case' },
            { value: 'assets', label: 'A set of individual assets' },
          ]}
        />

        {source === 'case' ? (
          <Select
            label={t('disclosure.new.field.case')}
            value={caseId}
            onChange={setCaseId}
            options={[
              { value: '', label: cases.isLoading ? 'Loading cases…' : 'Choose a case…' },
              ...openCases.map((c) => ({
                value: c.id,
                label: `${c.title} · ${c.linked_count} asset${c.linked_count === 1 ? '' : 's'}`,
              })),
            ]}
          />
        ) : (
          <fieldset className="border-0 p-0">
            <legend className="mb-1.5">
              <Label>{t('common.committedEvidence')}</Label>
            </legend>
            {storedAssets.length === 0 ? (
              <p className="text-[14px] text-[var(--ink-2)]">
                {assets.isLoading
                  ? t('common.loadingCommitted')
                  : 'No committed evidence to disclose yet.'}
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {storedAssets.map((a: EvidenceAsset) => (
                  <label key={a.id} className="flex items-center gap-2 text-[14px]">
                    <input
                      type="checkbox"
                      checked={assetIds.has(a.id)}
                      onChange={() => toggleAsset(a.id)}
                    />
                    <span>
                      {a.media_type} · <Mono>{a.merkle_root.slice(0, 12)}…</Mono> ·{' '}
                      {formatWhen(a.captured_at)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        )}

        <Field
          label={t('disclosure.new.field.expires')}
          value={expiresAt}
          onChange={setExpiresAt}
          type="date"
          hint="After this date the certificate states the disclosure is no longer valid."
        />

        <div>
          <Button type="submit" variant="primary" disabled={!ready || create.isPending}>
            {create.isPending ? t('disclosure.new.sealing') : t('disclosure.new.seal')}
          </Button>
        </div>
      </form>
    </Section>
  );
}

function DisclosureView({
  orgId,
  disclosureId,
  canWrite,
  onBack,
}: {
  orgId: string;
  disclosureId: string;
  canWrite: boolean;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [bundle, setBundle] = useState<SealedBundle | null>(null);

  const detail = useQuery({
    queryKey: ['evidence-disclosure', orgId, disclosureId],
    queryFn: () => getDisclosure(orgId, disclosureId),
    retry: false,
  });

  const downloadBundle = useMutation({
    mutationFn: () => getDisclosureBundle(orgId, disclosureId),
    onSuccess: (b) => {
      setBundle(b);
      setError('');
    },
    onError: (e: Error) => setError(e.message),
  });

  const revoke = useMutation({
    mutationFn: () => revokeDisclosure(orgId, disclosureId, revokeReason.trim() || undefined),
    onSuccess: () => {
      setConfirmingRevoke(false);
      setRevokeReason('');
      setError('');
      void qc.invalidateQueries({ queryKey: ['evidence-disclosure', orgId, disclosureId] });
      void qc.invalidateQueries({ queryKey: ['evidence-disclosures', orgId] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const back = (
    <div className="mb-4">
      <Button variant="quiet" onClick={onBack}>
        {t('disclosure.back')}
      </Button>
    </div>
  );

  if (detail.isLoading) {
    return (
      <>
        {back}
        <Spinner />
      </>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <>
        {back}
        <Finding tone="alert">
          {(detail.error as Error | null)?.message ?? t('common.notFound')}
        </Finding>
      </>
    );
  }

  const d: DisclosureDetail = detail.data;
  const state = disclosureState(d.disclosure);

  return (
    <>
      {back}
      <Section
        title={`Disclosure to ${d.disclosure.recipient}`}
        action={<Status tone={STATE_TONE[state]}>{sentence(state)}</Status>}
      >
        <Details d={d.disclosure} />
      </Section>

      {error ? <Finding tone="alert">{error}</Finding> : null}

      <Section title={t('disclosure.items.title')}>
        <Table
          head={[
            t('disclosure.items.head.asset'),
            t('disclosure.items.head.contentRef'),
            t('disclosure.items.head.derivativeOf'),
          ]}
        >
          {d.items.map((it) => (
            <Row key={it.asset_id}>
              <Cell>
                <Mono>{shortId(it.asset_id)}</Mono>
              </Cell>
              <Cell wide>
                <Mono>{it.delivered_sha256}</Mono>
              </Cell>
              <Cell>
                {it.derivative_of ? <Mono>{shortId(it.derivative_of)}</Mono> : 'Original'}
              </Cell>
            </Row>
          ))}
        </Table>
        <p className="mt-2 text-[13px] text-[var(--ink-2)]">
          The content reference is each asset’s RFC 6962 SHA-256 merkle root — the same value the
          ingest manifest binds and the transparency log anchors, not a plain file digest.
        </p>
      </Section>

      <Section title={t('disclosure.bundle.title')}>
        <Finding tone="attention">{VERDICT_NOTE}</Finding>
        <p className="mb-3 max-w-prose text-[14px] text-[var(--ink-2)]">
          The bundle carries every disclosed asset’s signed manifests, custody, and inclusion proofs
          under the current signed tree head, plus a signed plain-English certificate. Anyone can
          run it through the same offline verifier the defence would use.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button disabled={downloadBundle.isPending} onClick={() => downloadBundle.mutate()}>
            {downloadBundle.isPending
              ? t('disclosure.bundle.building')
              : bundle
                ? t('disclosure.bundle.rebuild')
                : t('disclosure.bundle.build')}
          </Button>
          {bundle ? (
            <Button
              variant="secondary"
              onClick={() =>
                download(`disclosure-${shortId(d.disclosure.id, 8)}-bundle.json`, bundle)
              }
            >
              {t('disclosure.bundle.download')}
            </Button>
          ) : null}
        </div>
        {bundle ? <BundleCertificate bundle={bundle} /> : null}
      </Section>

      {canWrite && state !== 'revoked' ? (
        <Section title={t('disclosure.revoke.title')}>
          <p className="mb-3 max-w-prose text-[14px] text-[var(--ink-2)]">
            Revoking marks this disclosure as no longer valid and writes a revocation custody event
            on every disclosed asset. It is irreversible — the record of the revocation is
            permanent, and it does not recall anything already handed over.
          </p>
          {confirmingRevoke ? (
            <div className="flex max-w-[560px] flex-col gap-3">
              <Field
                label="Reason (optional)"
                value={revokeReason}
                onChange={setRevokeReason}
                placeholder="Why it is being revoked"
                hint="Recorded on the certificate and in the custody chain."
              />
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  danger
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate()}
                >
                  {revoke.isPending
                    ? t('disclosure.revoke.revoking')
                    : t('disclosure.revoke.confirm')}
                </Button>
                <Button variant="quiet" onClick={() => setConfirmingRevoke(false)}>
                  {t('disclosure.revoke.keep')}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="secondary" danger onClick={() => setConfirmingRevoke(true)}>
              {t('disclosure.revoke.cta')}
            </Button>
          )}
        </Section>
      ) : null}
    </>
  );
}

function Details({ d }: { d: DisclosureRow }) {
  return (
    <div className="grid max-w-[720px] grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-[14px]">
      <Label>Identifier</Label>
      <Mono>{d.id}</Mono>
      <Label>Recipient</Label>
      <span>{d.recipient}</span>
      <Label>Purpose</Label>
      <span>{d.purpose}</span>
      <Label>Method</Label>
      <span>{d.method}</span>
      <Label>Disclosed</Label>
      <span>{formatWhen(d.created_at)}</span>
      <Label>Expires</Label>
      <span>{d.expires_at ? formatWhen(d.expires_at) : 'No expiry set'}</span>
      {d.revoked_at ? (
        <>
          <Label>Revoked</Label>
          <span>
            {formatWhen(d.revoked_at)}
            {d.revocation_reason ? ` · ${d.revocation_reason}` : ''}
          </span>
        </>
      ) : null}
    </div>
  );
}

function BundleCertificate({ bundle }: { bundle: SealedBundle }) {
  const cert = bundle.certificate;
  return (
    <div className="mt-4 border-t border-[var(--line)] pt-4">
      <h3 className="mb-2 text-[14px] leading-[19px] font-[650]">{cert.title}</h3>
      <div className="mb-3 grid max-w-[720px] grid-cols-[160px_1fr] gap-x-4 gap-y-1 text-[13px]">
        <Label>Verdict ceiling</Label>
        <span>{bundle.verifier.verdict_ceiling}</span>
        <Label>Log witnessed</Label>
        <span>{cert.transparency_log.witnessed ? 'Yes' : 'No — unwitnessed (v1)'}</span>
        <Label>Tree size</Label>
        <span className="tabular">{cert.transparency_log.tree_size}</span>
      </div>
      {/* The certificate's own statements, rendered verbatim — the service is
          the authority on exactly what it does and does not prove. */}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {cert.statements.map((s, i) => (
          <li key={i} className="max-w-prose text-[13px] leading-[19px] text-[var(--ink-2)]">
            {s}
          </li>
        ))}
      </ul>
    </div>
  );
}
